import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const InquiryForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    eventType: '',
    eventDate: '',
    guestCount: '',
    mealService: '',
    budget: '',
    message: '',
    dietaryRestrictions: []
  });

  const [submitted, setSubmitted] = useState(false);

  const eventTypeOptions = [
    { value: 'corporate-meeting', label: 'Corporate Meeting' },
    { value: 'conference', label: 'Conference/Seminar' },
    { value: 'wedding', label: 'Wedding Reception' },
    { value: 'birthday', label: 'Birthday Party' },
    { value: 'anniversary', label: 'Anniversary' },
    { value: 'holiday-party', label: 'Holiday Party' },
    { value: 'other', label: 'Other Event' }
  ];

  const mealServiceOptions = [
    { value: 'breakfast', label: 'Breakfast Service' },
    { value: 'lunch', label: 'Lunch Service' },
    { value: 'dinner', label: 'Dinner Service' },
    { value: 'appetizers', label: 'Appetizers Only' },
    { value: 'dessert', label: 'Dessert Bar' },
    { value: 'full-day', label: 'Full Day Service' }
  ];

  const budgetOptions = [
    { value: 'under-1000', label: 'Under $1,000' },
    { value: '1000-2500', label: '$1,000 - $2,500' },
    { value: '2500-5000', label: '$2,500 - $5,000' },
    { value: '5000-10000', label: '$5,000 - $10,000' },
    { value: 'over-10000', label: 'Over $10,000' }
  ];

  const dietaryOptions = [
    { id: 'vegetarian', label: 'Vegetarian Options' },
    { id: 'vegan', label: 'Vegan Options' },
    { id: 'gluten-free', label: 'Gluten-Free Options' },
    { id: 'dairy-free', label: 'Dairy-Free Options' },
    { id: 'nut-free', label: 'Nut-Free Options' },
    { id: 'halal', label: 'Halal Options' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDietaryChange = (optionId, checked) => {
    setFormData(prev => ({
      ...prev,
      dietaryRestrictions: checked
        ? [...prev?.dietaryRestrictions, optionId]
        : prev?.dietaryRestrictions?.filter(id => id !== optionId)
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        eventType: '',
        eventDate: '',
        guestCount: '',
        mealService: '',
        budget: '',
        message: '',
        dietaryRestrictions: []
      });
    }, 3000);
  };

  if (submitted) {
    return (
      <section id="inquiry-form" className="py-16 bg-card">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle" size={40} color="var(--color-success)" />
            </div>
            <h2 className="font-serif text-3xl font-bold text-foreground mb-4">
              Thank You for Your Inquiry!
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              We've received your catering request and will contact you within 24 hours to discuss your event details and provide a customized quote.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="outline" iconName="Phone" iconPosition="left">
                Call Us: (416) 555-0123
              </Button>
              <Button variant="default" iconName="Mail" iconPosition="left">
                Email: catering@montesanto.com
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="inquiry-form" className="py-16 bg-card">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 mb-4">
              <Icon name="Send" size={18} color="var(--color-primary)" />
              <span className="text-sm font-medium text-primary">Get Started</span>
            </div>
            <h2 className="font-serif text-4xl font-bold text-foreground mb-4">
              Request Your Catering Quote
            </h2>
            <p className="text-lg text-muted-foreground">
              Fill out the form below and we'll create a customized proposal for your event
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-background rounded-2xl shadow-warm p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Input
                label="Full Name"
                type="text"
                placeholder="Enter your name"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                required
              />

              <Input
                label="Email Address"
                type="email"
                placeholder="your.email@company.com"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                required
              />

              <Input
                label="Phone Number"
                type="tel"
                placeholder="(416) 555-0123"
                value={formData?.phone}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
                required
              />

              <Input
                label="Company/Organization"
                type="text"
                placeholder="Company name (optional)"
                value={formData?.company}
                onChange={(e) => handleInputChange('company', e?.target?.value)}
              />

              <Select
                label="Event Type"
                placeholder="Select event type"
                options={eventTypeOptions}
                value={formData?.eventType}
                onChange={(value) => handleInputChange('eventType', value)}
                required
              />

              <Input
                label="Event Date"
                type="date"
                value={formData?.eventDate}
                onChange={(e) => handleInputChange('eventDate', e?.target?.value)}
                required
              />

              <Input
                label="Number of Guests"
                type="number"
                placeholder="Expected guest count"
                value={formData?.guestCount}
                onChange={(e) => handleInputChange('guestCount', e?.target?.value)}
                required
              />

              <Select
                label="Meal Service"
                placeholder="Select meal type"
                options={mealServiceOptions}
                value={formData?.mealService}
                onChange={(value) => handleInputChange('mealService', value)}
                required
              />

              <Select
                label="Budget Range"
                placeholder="Select budget range"
                options={budgetOptions}
                value={formData?.budget}
                onChange={(value) => handleInputChange('budget', value)}
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-3">
                Dietary Requirements
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {dietaryOptions?.map((option) => (
                  <Checkbox
                    key={option?.id}
                    label={option?.label}
                    checked={formData?.dietaryRestrictions?.includes(option?.id)}
                    onChange={(e) => handleDietaryChange(option?.id, e?.target?.checked)}
                  />
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                Additional Details
              </label>
              <textarea
                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300 min-h-[120px]"
                placeholder="Tell us more about your event, special requests, or any questions you have..."
                value={formData?.message}
                onChange={(e) => handleInputChange('message', e?.target?.value)}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                type="submit"
                variant="default"
                size="lg"
                iconName="Send"
                iconPosition="left"
                fullWidth
              >
                Submit Inquiry
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                iconName="Phone"
                iconPosition="left"
              >
                Call Instead
              </Button>
            </div>

            <div className="mt-6 p-4 bg-accent/10 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
                <p className="text-sm text-foreground">
                  <strong>Response Time:</strong> We typically respond to catering inquiries within 24 hours. For urgent requests, please call us directly at (416) 555-0123.
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default InquiryForm;